import { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { t } from "i18next";
import { Typography } from "@mui/material";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import "./TurnBurst.css";

export interface TurnBurstProps {
  isVisible: boolean;
  isTsar: boolean;
  tsarName: string;
  onDismiss: () => void;
  mode?: string;
}

const BURST_MESSAGES: Record<string, { headlineTsar: string; headlineOther: string; subtitleTsar: string; subtitleOther: string }> = {
  STANDARD: {
    headlineTsar: "intro_for_tsar",
    headlineOther: "intro_about_tsar",
    subtitleTsar: "burst_subtitle_tsar",
    subtitleOther: "burst_subtitle_other",
  },
  FOLLOW_THE_LEADER: {
    headlineTsar: "ftl_burst_headline_tsar",
    headlineOther: "ftl_burst_headline_other",
    subtitleTsar: "ftl_burst_subtitle_tsar",
    subtitleOther: "ftl_burst_subtitle_other",
  },
  REVERSE_ROUND: {
    headlineTsar: "rr_burst_headline",
    headlineOther: "rr_burst_headline",
    subtitleTsar: "rr_burst_subtitle",
    subtitleOther: "rr_burst_subtitle",
  },
  REVERSE_ROUND_GUESS: {
    headlineTsar: "rr_guess_burst_headline",
    headlineOther: "rr_guess_burst_headline",
    subtitleTsar: "rr_guess_burst_subtitle",
    subtitleOther: "rr_guess_burst_subtitle",
  },
  RUN_WITH_THE_HERD: {
    headlineTsar: "herd_burst_headline",
    headlineOther: "herd_burst_headline",
    subtitleTsar: "herd_burst_subtitle",
    subtitleOther: "herd_burst_subtitle",
  },
  STANDARD_VERSUS: {
    headlineTsar: "versus_burst_headline",
    headlineOther: "versus_burst_headline",
    subtitleTsar: "versus_burst_subtitle",
    subtitleOther: "versus_burst_subtitle",
  },
  PICASSO: {
    headlineTsar: "picasso_burst_headline",
    headlineOther: "picasso_burst_headline",
    subtitleTsar: "picasso_burst_subtitle",
    subtitleOther: "picasso_burst_subtitle",
  },
  PICASSO_GALLERY: {
    headlineTsar: "picasso_gallery_burst_headline",
    headlineOther: "picasso_gallery_burst_headline",
    subtitleTsar: "picasso_gallery_burst_subtitle",
    subtitleOther: "picasso_gallery_burst_subtitle",
  },
};

export default function TurnBurst({ isVisible, isTsar, tsarName, onDismiss, mode = "STANDARD" }: TurnBurstProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isVisible) return;
    const msgs = BURST_MESSAGES[mode] ?? BURST_MESSAGES.STANDARD;
    const subtitleKey = isTsar ? msgs.subtitleTsar : msgs.subtitleOther;
    const words = t(subtitleKey, { tsar: tsarName }).split(" ");
    const dismissMs = words.length > 10 ? 4000 : 3000;
    timerRef.current = setTimeout(() => onDismiss(), dismissMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isVisible, mode, isTsar]);

  if (!isVisible) return null;

  const msgs = BURST_MESSAGES[mode] ?? BURST_MESSAGES.STANDARD;
  const headlineKey = isTsar ? msgs.headlineTsar : msgs.headlineOther;
  const subtitleKey = isTsar ? msgs.subtitleTsar : msgs.subtitleOther;
  const subtitleWords = t(subtitleKey, { tsar: tsarName }).split(" ");

  return (
    <div className="turn-burst-overlay" onClick={onDismiss}>
      {/* Headline */}
      <Typography
        className="turn-burst-headline"
        variant="h3"
        color="white"
        fontWeight="bold"
        sx={{ fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" } }}
      >
        <Trans
          i18nKey={headlineKey}
          components={{
            h: (
              <Typography
                component="span"
                variant="h3"
                color="primary"
                sx={{
                  fontWeight: "inherit",
                  fontSize: "inherit",
                }}
              />
            ),
          }}
          values={{ tsar: tsarName }}
        />
      </Typography>

      {/* Icon */}
      <TouchAppIcon
        className="turn-burst-icon"
        sx={{ fontSize: { xs: 40, md: 56 }, color: "primary.main" }}
      />

      {/* Subtitle — word-by-word bounce, tsar name highlighted */}
      <Typography
        variant="h5"
        color="white"
        sx={{
          fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
          textAlign: "center",
          px: 2,
        }}
      >
        {(() => {
          // Use marker to locate tsar name in the interpolated string
          const marked = t(subtitleKey, { tsar: `\x00${tsarName}\x00` });
          const parts = marked.split("\x00"); // [before, tsarName, after]
          let wordIndex = 0;
          return parts.map((part, pi) => {
            const isName = pi === 1;
            const words = part.split(" ").filter(Boolean);
            return words.map((word) => {
              const idx = wordIndex++;
              return (
                <span
                  key={idx}
                  className="turn-burst-word"
                  style={{
                    animationDelay: `${0.8 + idx * 0.12}s`,
                    marginRight: "0.3em",
                    color: isName ? "#90caf9" : undefined,
                    fontWeight: isName ? "bold" : undefined,
                  }}
                >
                  {word}
                </span>
              );
            });
          });
        })()}
      </Typography>
    </div>
  );
}
